
    import { computed, defineComponent, ref } from 'vue'
    import { Route } from '../enums/Route'
    import { Theme } from '../enums/Theme'
    import PartnersNew from '../components/PartnersNew.vue'
    import ClippingAnimation from '../components/ClippingAnimation.vue'
    import CustomCursor from '../components/CustomCursor.vue'
    import Picture from '../components/Picture.vue'
    import Anchor from '../components/Anchor.vue'
    import { useCms } from '../compositions/UseCms'
    import fallbackCaseStudies from '../cms/cn/en/cases.json'
    import fallbackBlogs from '../cms/cn/en/blogs.json'

    interface Faq {
        question: string
        answer: string[]
    }

    interface BlogPost {
        image: string
        gradient?: string
        date: string
        readTime: string
        author: string
        title: string
    }

    export default defineComponent({
        name: 'ServicesNew',
        components: {
            PartnersNew,
            ClippingAnimation,
            CustomCursor,
            Picture,
            Anchor
        },
        setup() {

            const { caseStudies, home, blogs } = useCms()
            const openFaq = ref<number | null>(0)
            const currentBlogIndex = ref<number>(0)

            const projects = computed(() => {

                const cmsProjects = caseStudies.value || home.value?.related_cases || []

                return cmsProjects.length ? cmsProjects.slice(0, 6) : fallbackCaseStudies.slice(0, 6)

            })

            const blogPosts = computed(() => {

                const cmsBlogs = blogs.value || []
                return cmsBlogs.length ? cmsBlogs : fallbackBlogs

            })

            const hasBlogCarousel = computed(() => blogPosts.value.length > 3)
            const maxBlogIndex = computed(() => Math.max(0, blogPosts.value.length - 3))

            const nextBlog = () => {

                if (hasBlogCarousel.value && currentBlogIndex.value < maxBlogIndex.value) {

                    currentBlogIndex.value++

                }

            }

            const prevBlog = () => {

                if (hasBlogCarousel.value && currentBlogIndex.value > 0) {

                    currentBlogIndex.value--

                }

            }

            const faqs: Faq[] = [
                {
                    question: 'Why are WeChat mini programs popular?',
                    answer: [
                        'WeChat mini programs don\'t require downloading and installing like traditional apps, with many quick and easy entry points such as social sharing and scanning a QR code. With 78% of the population in China active monthly users of WeChat, there is an incredibly frictionless barrier to entry with functions like WeChat login and WeChat pay being integrated seamlessly in mini programs.',
                        'The app-like user experience available without ever leaving WeChat or needing to make new user accounts for each app makes them highly convenient.'
                    ]
                },
                {
                    question: 'What are WeChat mini programs?',
                    answer: [
                        'The Chinese social media and messaging app WeChat offers WeChat Mini Programs as a core feature. These mini-apps run within WeChat\'s ecosystem and are lightweight and fast-loading, providing users with direct access to various services and features within WeChat, such as payments, sharing, login, and much more.',
                        'WeChat Mini Programs are developed using web technologies and behave similarly to mobile websites (with an app like UX), allowing them to run without downloading or installing and without needing app stores.'
                    ]
                },
                {
                    question: 'When to use a mini program vs mobile app?',
                    answer: [
                        'WeChat mini programs are ideal for infrequently used, single-task, or social-driven user experiences - such as ordering an express delivery service on SF Express and then sharing the tracking number with the receiver on WeChat.',
                        'Mobile apps are better suited for regular-use experiences that warrant leaving WeChat and using a single application - such as ordering meals on Ele.me or drivers on DiDi. However, both these examples have mini programs, most commonly leveraged for social aspects, such as group orders on Ele.me or splitting a bill on DiDi, shared from the app to WeChat.'
                    ]
                },
                {
                    question: 'What programming language are WeChat mini programs made in?',
                    answer: [
                        'WeChat mini programs are developed using JavaScript and WXS (WeChat Script) combined with WXSS (WeChat Style Sheets) and WXML code. WXS is similar to Javascript but has its own syntax.',
                        'WXSS is WeChat\'s answer to CSS, and WXML is used to build the page structure with base components.'
                    ]
                }
            ]

            const toggleFaq = (index: number) => {

                openFaq.value = openFaq.value === index ? null : index

            }

            return {
                Route,
                Theme,
                projects,
                faqs,
                blogPosts,
                hasBlogCarousel,
                currentBlogIndex,
                nextBlog,
                prevBlog,
                openFaq,
                toggleFaq
            }

        }
    })
